import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { circuitsForSport } from "@/lib/sports";
import { useCircuitConfig } from "@/lib/circuits";
import { invalidateUnverified } from "@/lib/circuitStatus";
import FlagButton from "@/components/FlagButton";
import { Check, ChevronDown, Plus } from "lucide-react";
import { toast } from "sonner";

/** Combined circuit selector: main circuits (admin + verified) then unverified user circuits (flagged), with an inline "Other:" add box. */
export default function CircuitSelect({ sport, multi = false, value, onChange, accent = "#ff4500", testid = "circuit-select", leadOptions = [], widthClass = "w-full", placeholder = "Select circuits" }) {
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [userCircuits, setUserCircuits] = useState([]);
    const [q, setQ] = useState("");
    const [other, setOther] = useState("");
    const [busy, setBusy] = useState(false);
    const ref = useRef(null);
    const cfg = useCircuitConfig(sport);

    const selected = multi ? (value || []) : (value ? [value] : []);

    const load = () => api.get(`/circuits/user?sport=${sport}`).then((r) => setUserCircuits(r.data)).catch(() => setUserCircuits([]));
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [sport]);
    useEffect(() => {
        const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", h);
        return () => document.removeEventListener("mousedown", h);
    }, []);

    const verifiedNames = userCircuits.filter((c) => c.verified).map((c) => c.name);
    const unverified = userCircuits.filter((c) => !c.verified);
    const leadItems = [...new Set(leadOptions)];

    // Grouped main circuits — mirrors the main-page CircuitFilter exactly: admin tier
    // groups (with their labels) in order, then an "Other circuits" group for anything
    // unsorted / verified. Falls back to a single "Main circuits" group when no tier
    // config exists. leadOptions (e.g. "N/A") render first, ungrouped.
    const tierGroups = (cfg.tiers || [])
        .map((t) => ({ label: t.name, items: (t.circuits || []).filter((c) => !leadItems.includes(c)) }))
        .filter((g) => g.items.length);
    const usedInTiers = new Set(tierGroups.flatMap((g) => g.items));
    const unsortedItems = [...new Set([...(cfg.unsorted || []), ...verifiedNames])]
        .filter((c) => c && !leadItems.includes(c) && !usedInTiers.has(c));
    let mainGroups;
    if (tierGroups.length || unsortedItems.length) {
        mainGroups = tierGroups.slice();
        if (unsortedItems.length) mainGroups.push({ label: mainGroups.length ? "Other circuits" : "Main circuits", items: unsortedItems });
    } else {
        const flat = (cfg.main && cfg.main.length) ? cfg.main : circuitsForSport(sport);
        mainGroups = [{ label: "Main circuits", items: [...new Set(flat)].filter((c) => !leadItems.includes(c)) }];
    }

    const toggle = (name) => {
        if (multi) onChange(selected.includes(name) ? selected.filter((c) => c !== name) : [...selected, name]);
        else { onChange(name); setOpen(false); }
    };

    const addOther = async () => {
        const v = other.trim();
        if (!v || busy) return;
        if (!user) return toast.error("Log in to add a circuit");
        setBusy(true);
        let data;
        try {
            const res = await api.post("/circuits/user", { sport, name: v });
            data = res.data;
        } catch (e) {
            const detail = e?.response?.data?.detail;
            toast.error(detail || (e?.response ? "Could not add circuit" : "Network error — check your connection and try again"));
            setBusy(false);
            return;
        }
        // Post-success UI updates — never surface these as an "add failed" error.
        try {
            await load();
            invalidateUnverified(sport);
            if (data?.name && !selected.includes(data.name)) toggle(data.name);
            setOther("");
        } catch (_) { /* ignore refresh hiccups; the circuit was added */ }
        toast.success(`Added "${data?.name || v}"`);
        setBusy(false);
    };

    const term = q.trim().toLowerCase();
    const matches = (s) => s.toLowerCase().includes(term);
    const leadF = leadItems.filter(matches);
    const groupsF = mainGroups
        .map((g) => ({ label: g.label, items: g.items.filter(matches) }))
        .filter((g) => g.items.length);
    const unverifiedF = unverified.filter((c) => c.name.toLowerCase().includes(term));

    const Row = ({ name, uc }) => (
        <div className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-[#181818]" data-testid={`${testid}-opt-${name}`} onClick={() => toggle(name)}>
            <span className={`h-4 w-4 border rounded-sm flex items-center justify-center flex-none ${selected.includes(name) ? "border-transparent" : "border-[#444]"}`} style={selected.includes(name) ? { background: accent } : {}}>
                {selected.includes(name) && <Check className="h-3 w-3 text-white" />}
            </span>
            <span className="text-white">{name}</span>
            {uc && (
                <span className="ml-auto flex-none" onClick={(e) => e.stopPropagation()} data-testid={`${testid}-report-wrap-${uc.id}`}>
                    <FlagButton targetType="circuit" targetId={uc.id} label="" testid={`${testid}-report-${uc.id}`} />
                </span>
            )}
        </div>
    );

    return (
        <div className={`relative ${widthClass}`} ref={ref} data-testid={testid}>
            <button type="button" data-testid={`${testid}-trigger`} onClick={() => setOpen((o) => !o)}
                className="h-11 w-full flex items-center justify-between px-3 border border-[#2a2a2a] bg-[#0f0f0f] text-sm">
                <span className={selected.length ? "text-white" : "text-[#666]"}>{selected.length ? selected.join(", ") : placeholder}</span>
                <ChevronDown className="h-4 w-4 text-[#a0a0a0]" />
            </button>
            {open && (
                <div className="absolute z-40 mt-1 w-full min-w-[15rem] border border-[#2a2a2a] bg-[#0f0f0f] shadow-2xl">
                    <div className="p-2 border-b border-[#2a2a2a]">
                        <input data-testid={`${testid}-search`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search circuits…" className="w-full min-w-0 h-8 bg-[#141414] border border-[#2a2a2a] px-2 text-sm text-white outline-none" />
                    </div>
                    {leadF.length > 0 && leadF.map((c) => <Row key={c} name={c} />)}
                    {groupsF.map((g, gi) => (
                        <div key={`${g.label}-${gi}`} data-testid={`${testid}-group-${gi}`}>
                            <div className={`px-3 pt-2 pb-1 text-[9px] uppercase tracking-[0.18em] bg-[#0c0c0c] ${g.label === "Main circuits" || g.label === "Other circuits" ? "text-[#666]" : "text-[#f59e0b]"}`}>{g.label}</div>
                            {g.items.map((c) => <Row key={c} name={c} />)}
                        </div>
                    ))}
                    {unverifiedF.length > 0 && <div className="px-3 pt-2 pb-1 text-[9px] uppercase tracking-[0.18em] text-[#666] bg-[#0c0c0c]">Other circuits (added by users)</div>}
                    {unverifiedF.map((c) => <Row key={c.id} name={c.name} uc={c} />)}
                    <div className="border-t border-[#2a2a2a] p-2.5 bg-[#0c0c0c]">
                        <div className="text-[10px] uppercase tracking-[0.15em] text-[#a0a0a0] mb-1.5">Other:</div>
                        <div className="flex gap-1.5">
                            <input data-testid={`${testid}-other-input`} value={other} onChange={(e) => setOther(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addOther(); } }} placeholder="Type circuit name" maxLength={80} className="flex-1 min-w-0 h-8 bg-[#141414] border border-[#2a2a2a] px-2 text-sm text-white outline-none" />
                            <button type="button" data-testid={`${testid}-other-add`} onClick={addOther} disabled={busy || !other.trim()} className="flex-none h-8 px-3 text-white text-[10px] font-bold uppercase tracking-wider disabled:opacity-40" style={{ background: accent }}><Plus className="h-3 w-3" /></button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
